/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useCallback, useContext } from 'react'

import {
  AbilityContext,
  useCheckOperation,
  UUID,
  useCurrentUser,
} from '@builddocs/data-management'
import {
  Application,
  deleteStamp,
  useTasksOfDocument,
  useTasksSignStamps,
} from '@builddocs/filestore-api'
import { useToast } from '@builddocs/system'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { AxiosError } from 'axios'

import { StatusStamp } from './StatusStamp'

export interface StampsOnDocumentProps {
  docId: string
  documentType: Application
  creatorId?: UUID
  sourceId?: string
  showDescription?: boolean
  hideDeleteStampsButton?: boolean
  onUpdatePDF?: () => void
}

export function StampsOnDocument({
  docId,
  documentType,
  creatorId,
  sourceId,
  showDescription,
  hideDeleteStampsButton,
  onUpdatePDF,
}: StampsOnDocumentProps) {
  const { data } = useTasksOfDocument(docId)
  const { data: parentDocumentData } = useTasksOfDocument(
    sourceId,
    documentType === 'EXECUTIVE_DRAWING',
  )
  const { toast } = useToast()

  const { user } = useCurrentUser()
  const ability = useContext(AbilityContext)

  const { refresh: refreshTasksOfDocument } = useTasksOfDocument(docId)

  const { refresh: refreshTaskSigningStamps } = useTasksSignStamps(
    user.employment.employmentId,
  )

  const { checkOperation } = useCheckOperation()

  const isDocCreator = user.userId === creatorId

  const canEdit = ability.can('edit', documentType) && isDocCreator

  const refresh = useCallback(() => {
    refreshTaskSigningStamps()
    refreshTasksOfDocument()
  }, [refreshTaskSigningStamps, refreshTasksOfDocument])

  const handleDeleteStamp = () => {
    if (window.confirm('Действительно удалить все штампы с документа?')) {
      deleteStamp(null, docId)
        .then(() => {
          refresh()
          toast.success('Штампы удалены')
          onUpdatePDF && onUpdatePDF()
        })
        .catch(error => {
          const e = error as AxiosError

          toast.warning(
            e?.response?.data?.message ?? 'Не удалось удалить штампы',
          )
        })
    }
  }

  if (!data?.length && showDescription && !parentDocumentData?.length) {
    return <Typography variant="h6">На документе нет штампов</Typography>
  }

  const handleDeleteStampClick = () => {
    checkOperation({
      operationId: 'STAMP_DELETE',
      operationCallback: handleDeleteStamp,
    })
  }

  return (
    <>
      {showDescription && (
        <Typography gutterBottom variant="h6">
          Штампы на документе
        </Typography>
      )}
      <Stack alignItems="center" direction="row">
        <Stack gap="8px">
          {parentDocumentData?.map(signingTask => (
            <StatusStamp
              key={signingTask.id}
              docId={sourceId!}
              documentType={documentType}
              refresh={refresh}
              signingTask={signingTask}
              user={user}
              onUpdatePDF={onUpdatePDF}
            />
          ))}
          {data?.map(signingTask => (
            <StatusStamp
              key={signingTask.id}
              docId={docId}
              documentType={documentType}
              refresh={refresh}
              signingTask={signingTask}
              user={user}
              onUpdatePDF={onUpdatePDF}
            />
          ))}
        </Stack>
        {!hideDeleteStampsButton && canEdit && (
          <Box>
            {data && data.length > 0 && (
              <Tooltip title="Удалить все штампы">
                <IconButton onClick={handleDeleteStampClick}>
                  <DeleteOutlineRoundedIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Stack>
    </>
  )
}

export default StampsOnDocument
